import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Check,
  ChevronDown,
  Loader2,
  Mail,
  Megaphone,
  MessageSquare,
  Pin,
  Search,
  Send,
  Sparkles,
  Users,
  X,
} from 'lucide-react';
import { CommunicationInbox } from '../components/CommunicationInbox';
import { AdminModulePage, ConfirmDialog, LoadingButton, PageHero } from '../components/PageTemplates';
import { useOverlayScrollLock } from '../hooks/useOverlayScrollLock';
import { useStore } from '../hooks/useStore';
import { type ActiveCommunicationChannel, type Profile } from '../models';
import { ALL_ROLES, ROLE_LABEL, type Role } from '../models/user';
import { setDb, type AppDatabase } from '../services/store';
import {
  type AudiencePreview,
  canMessageTournament,
  getAudiencePreview,
  listCommunications,
  listMessageableTournaments,
} from '../services/communicationHubService';
import { useActiveRole } from '../state/RoleContext';
import { useAuth } from '../state/AuthContext';

type HubTab = 'compose' | 'inbox' | 'sent';
type TargetMode = 'league' | 'event';
type RecipientChip =
  | { key: string; kind: 'directory'; id: 'directory'; label: string }
  | { key: string; kind: 'event'; id: string; label: string }
  | { key: string; kind: 'person'; id: string; label: string }
  | { key: string; kind: 'role'; id: Role; label: string }
  | { key: string; kind: 'division'; id: string; label: string }
  | { key: string; kind: 'team'; id: string; label: string };

interface RecipientPersonOption {
  id: string;
  displayName: string;
  email?: string;
  roleLabels: string[];
  lastEventName?: string;
}

const COMPOSER_LEAVE_WARNING = 'Leave Communication Hub? Your draft and recipient selections will be lost.';
const RECIPIENT_PREVIEW_LIMIT = 8;

const EMPTY_AUDIENCE_PREVIEW: AudiencePreview = {
  items: [],
  sendableRecipients: [],
  warningRecipients: [],
  blockedRecipients: [],
  emailRecipients: [],
  inAppRecipients: [],
  skippedDeliveries: 0,
};

interface SendResponse {
  ok?: boolean;
  db?: AppDatabase;
  error?: string;
  summary?: {
    contactableRecipients: number;
    emailQueued: number;
    inAppSent: number;
    skippedDeliveries: number;
    failedDeliveries: number;
  };
}

interface AssistResponse {
  ok?: boolean;
  error?: string;
  detail?: string;
  draft?: {
    subject: string;
    body: string;
    notes: string;
  };
}

function communicationAssistError(data: AssistResponse, status: number): string {
  if (data.error === 'openai_not_configured') {
    return 'Scorekeeper Intelligence is not configured yet. Add the OpenAI API key in Cloudflare and try again.';
  }
  if (data.error === 'brief_required') {
    return 'Tell Scorekeeper Intelligence what you want to send first.';
  }
  if (data.error === 'openai_request_failed') {
    return `Scorekeeper Intelligence could not generate this draft. ${data.detail ?? `OpenAI returned ${status}.`}`;
  }
  if (data.error === 'openai_empty_response' || data.error === 'openai_incomplete_response' || data.error === 'openai_invalid_json') {
    return 'Scorekeeper Intelligence returned an unusable draft. Try a shorter instruction or type the message manually.';
  }
  if (data.error === 'unauthorized') {
    return 'Your session needs to be refreshed before generating a draft.';
  }
  return data.error ?? `Draft generation failed with ${status}.`;
}

function roleLabelsForProfile(db: AppDatabase, profile: Profile, tournamentId?: string): string[] {
  const roles = new Set<Role>(profile.globalRoles ?? []);
  if (tournamentId) {
    db.roleAssignments
      .filter((assignment) => assignment.profileId === profile.id && assignment.tournamentId === tournamentId)
      .forEach((assignment) => roles.add(assignment.role));
    if (db.teams.some((team) => team.tournamentId === tournamentId && team.captainProfileId === profile.id)) {
      roles.add('captain');
    }
    const playerIds = new Set(db.players.filter((player) => player.profileId === profile.id).map((player) => player.id));
    if (db.rosterEntries.some((entry) => entry.tournamentId === tournamentId && playerIds.has(entry.playerId))) {
      roles.add('player');
    }
  }
  return Array.from(roles).map((role) => ROLE_LABEL[role]).filter(Boolean).slice(0, 4);
}

function tournamentDateValue(tournament: AppDatabase['tournaments'][number]): number {
  return Date.parse(tournament.startsAt ?? tournament.endsAt ?? tournament.updatedAt ?? tournament.createdAt) || 0;
}

function lastEventNameForProfile(db: AppDatabase, profileId: string): string | undefined {
  const playerIds = new Set(db.players.filter((player) => player.profileId === profileId).map((player) => player.id));
  const tournamentIds = new Set<string>();
  db.registrations.filter((registration) => registration.profileId === profileId).forEach((registration) => tournamentIds.add(registration.tournamentId));
  db.roleAssignments.filter((assignment) => assignment.profileId === profileId).forEach((assignment) => tournamentIds.add(assignment.tournamentId));
  db.rosterEntries.filter((entry) => playerIds.has(entry.playerId)).forEach((entry) => tournamentIds.add(entry.tournamentId));
  db.teams.filter((team) => team.captainProfileId === profileId).forEach((team) => tournamentIds.add(team.tournamentId));
  const latest = db.tournaments
    .filter((tournament) => tournamentIds.has(tournament.id))
    .sort((a, b) => tournamentDateValue(b) - tournamentDateValue(a))[0];
  return latest?.name;
}

function recipientPersonOption(db: AppDatabase, profile: Profile, tournamentId?: string): RecipientPersonOption {
  return {
    id: profile.id,
    displayName: profile.displayName,
    email: profile.email,
    roleLabels: roleLabelsForProfile(db, profile, tournamentId),
    lastEventName: lastEventNameForProfile(db, profile.id),
  };
}

function looksLikeAssistInstruction(value: string): boolean {
  const text = value.trim();
  if (!text || text.length > 260 || text.includes('\n')) return false;
  return /\b(send|draft|write|create|generate|tell|announce|remind|email|message|let)\b/i.test(text);
}

export function CommunicationHubPage() {
  const { profile } = useActiveRole();
  const { canAdmin } = useAuth();
  const [searchParams] = useSearchParams();
  const db = useStore((d) => d);
  const eventIdParam = searchParams.get('eventId') ?? '';

  const messageableTournaments = useMemo(() => listMessageableTournaments(db), [db.tournaments]);
  const [targetMode, setTargetMode] = useState<TargetMode>('league');
  const [tournamentId, setTournamentId] = useState('');
  const selectedTournament = targetMode === 'event'
    ? messageableTournaments.find((t) => t.id === tournamentId)
    : undefined;
  const isSelectedMessageable = targetMode === 'league' || canMessageTournament(selectedTournament);
  const senderInDirectory = db.profiles.some((person) => person.id === profile.id && (person.directoryStatus ?? 'active') !== 'archived');
  const allowed = canAdmin && senderInDirectory && isSelectedMessageable && (targetMode === 'league' || Boolean(selectedTournament));

  const [tab, setTab] = useState<HubTab>('compose');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [recipientFlyoutOpen, setRecipientFlyoutOpen] = useState(false);
  const [allDirectorySelected, setAllDirectorySelected] = useState(false);
  const [roles, setRoles] = useState<Role[]>([]);
  const [teamIds, setTeamIds] = useState<string[]>([]);
  const [divisionIds, setDivisionIds] = useState<string[]>([]);
  const [profileIds, setProfileIds] = useState<string[]>([]);
  const [personQuery, setPersonQuery] = useState('');
  const [channels, setChannels] = useState<ActiveCommunicationChannel[]>(['email', 'in_app']);
  const [postAsAnnouncement, setPostAsAnnouncement] = useState(false);
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [assistBrief, setAssistBrief] = useState('');
  const [assisting, setAssisting] = useState(false);
  const [assistNotes, setAssistNotes] = useState('');
  const [leaveHref, setLeaveHref] = useState('');
  const [sendConfirmOpen, setSendConfirmOpen] = useState(false);
  useOverlayScrollLock(recipientFlyoutOpen);

  const teams = selectedTournament ? db.teams.filter((team) => team.tournamentId === selectedTournament.id) : [];
  const divisions = selectedTournament ? db.divisions.filter((division) => division.tournamentId === selectedTournament.id) : [];
  const messages = listCommunications();
  const people = useMemo<RecipientPersonOption[]>(() => {
    const query = personQuery.trim().toLowerCase();
    return db.profiles
      .filter((person) => (person.directoryStatus ?? 'active') !== 'archived')
      .filter((person) => {
        if (!query) return true;
        return `${person.displayName} ${person.email ?? ''}`.toLowerCase().includes(query);
      })
      .sort((a, b) => a.displayName.localeCompare(b.displayName))
      .slice(0, 40)
      .map((person) => recipientPersonOption(db, person, targetMode === 'event' ? selectedTournament?.id : undefined));
  }, [db, personQuery, selectedTournament?.id, targetMode]);

  useEffect(() => {
    if (!eventIdParam) return;
    const event = messageableTournaments.find((tournament) => tournament.id === eventIdParam);
    if (event) {
      setTargetMode('event');
      setTournamentId(event.id);
      setAllDirectorySelected(false);
    }
  }, [eventIdParam, messageableTournaments]);

  useEffect(() => {
    if (targetMode === 'event' && messageableTournaments.length > 0 && !messageableTournaments.some((t) => t.id === tournamentId)) {
      setTournamentId(messageableTournaments[0].id);
    }
  }, [messageableTournaments, targetMode, tournamentId]);

  useEffect(() => {
    if (targetMode === 'league') {
      setTeamIds([]);
      setDivisionIds([]);
      setPostAsAnnouncement(false);
    }
  }, [targetMode]);

  const audience = useMemo(
    () => ({
      roles,
      teamIds: targetMode === 'event' ? teamIds : [],
      divisionIds: targetMode === 'event' ? divisionIds : [],
      profileIds,
    }),
    [divisionIds, profileIds, roles, targetMode, teamIds],
  );
  const hasEventFilter = targetMode === 'event' && Boolean(selectedTournament);
  const hasRecipientFilters =
    hasEventFilter ||
    roles.length > 0 ||
    profileIds.length > 0 ||
    (targetMode === 'event' && (teamIds.length > 0 || divisionIds.length > 0));
  const hasRecipientCriteria = allDirectorySelected || hasRecipientFilters;
  const sendAudience = allDirectorySelected ? {} : audience;
  const rawAudiencePreview = useMemo(
    () => getAudiencePreview(audience, channels, db, { tournamentId: targetMode === 'event' ? selectedTournament?.id : undefined }),
    [audience, channels, db, targetMode, selectedTournament?.id],
  );
  const audiencePreview = hasRecipientCriteria ? rawAudiencePreview : EMPTY_AUDIENCE_PREVIEW;
  const deliveryWarnings =
    audiencePreview.warningRecipients.length + audiencePreview.blockedRecipients.length + audiencePreview.skippedDeliveries;
  const selectedRecipientCount = audiencePreview.items.length;
  const previewRecipients = useMemo(
    () => audiencePreview.sendableRecipients.map((item) =>
      recipientPersonOption(db, item.profile, targetMode === 'event' ? selectedTournament?.id : undefined),
    ),
    [audiencePreview.sendableRecipients, db, selectedTournament?.id, targetMode],
  );
  const selectedPeople = useMemo(
    () => profileIds.flatMap((id) => {
      const person = db.profiles.find((candidate) => candidate.id === id);
      return person ? [person] : [];
    }),
    [db.profiles, profileIds],
  );
  const targetLabel = targetMode === 'event'
    ? selectedTournament?.name ?? 'Event audience'
    : allDirectorySelected
    ? 'Full Directory'
    : 'Directory recipients';
  const hasCustomRecipients =
    allDirectorySelected ||
    roles.length > 0 ||
    profileIds.length > 0 ||
    teamIds.length > 0 ||
    divisionIds.length > 0 ||
    hasEventFilter;
  const hasUnsavedComposerProgress =
    tab === 'compose' &&
    !sending &&
    (Boolean(subject.trim()) ||
      Boolean(body.trim()) ||
      Boolean(assistBrief.trim()) ||
      Boolean(assistNotes.trim()) ||
      hasCustomRecipients);

  useEffect(() => {
    if (!hasUnsavedComposerProgress) return undefined;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [hasUnsavedComposerProgress]);

  useEffect(() => {
    if (!hasUnsavedComposerProgress) return undefined;
    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.altKey || event.ctrlKey || event.shiftKey) return;
      const target = event.target instanceof Element ? event.target : null;
      const anchor = target?.closest('a[href]') as HTMLAnchorElement | null;
      if (!anchor || (anchor.target && anchor.target !== '_self') || anchor.hasAttribute('download')) return;
      const url = new URL(anchor.href);
      const sameRoute = url.origin === window.location.origin && url.pathname === window.location.pathname && url.search === window.location.search;
      if (!sameRoute) {
        event.preventDefault();
        event.stopPropagation();
        setLeaveHref(anchor.href);
      }
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [hasUnsavedComposerProgress]);

  const resetComposer = () => {
    setSubject('');
    setBody('');
    setAllDirectorySelected(false);
    setRecipientFlyoutOpen(false);
    setRoles([]);
    setTeamIds([]);
    setDivisionIds([]);
    setProfileIds([]);
    setPersonQuery('');
    setChannels(['email', 'in_app']);
    setPostAsAnnouncement(false);
    setAssistBrief('');
    setAssistNotes('');
  };

  const clearRecipients = () => {
    setAllDirectorySelected(false);
    setTargetMode('league');
    setTournamentId('');
    setRoles([]);
    setTeamIds([]);
    setDivisionIds([]);
    setProfileIds([]);
    setPersonQuery('');
  };

  const toggleAllDirectory = () => {
    if (allDirectorySelected) {
      setAllDirectorySelected(false);
      return;
    }
    setAllDirectorySelected(true);
    setTargetMode('league');
    setTournamentId('');
    setRoles([]);
    setTeamIds([]);
    setDivisionIds([]);
    setProfileIds([]);
    setPersonQuery('');
  };

  const updateEventFilter = (id: string) => {
    if (!id) {
      setTargetMode('league');
      setTournamentId('');
      setTeamIds([]);
      setDivisionIds([]);
      setPostAsAnnouncement(false);
      setAllDirectorySelected(false);
      return;
    }
    setTargetMode('event');
    setTournamentId(id);
    setAllDirectorySelected(false);
  };

  const updateRoles = (next: Role[]) => {
    setRoles(next);
    setAllDirectorySelected(false);
  };
  const updateDivisionIds = (next: string[]) => {
    setDivisionIds(next);
    setAllDirectorySelected(false);
  };
  const updateTeamIds = (next: string[]) => {
    setTeamIds(next);
    setAllDirectorySelected(false);
  };
  const updateProfileIds = (next: string[]) => {
    setProfileIds(next);
    setAllDirectorySelected(false);
  };

  const recipientChips: RecipientChip[] = allDirectorySelected
    ? [{ key: 'directory:all', kind: 'directory' as const, id: 'directory', label: 'Everyone in Directory' }]
    : [
        ...(hasEventFilter && selectedTournament
          ? [{ key: `event:${selectedTournament.id}`, kind: 'event' as const, id: selectedTournament.id, label: selectedTournament.name }]
          : []),
        ...selectedPeople.map((person) => ({ key: `person:${person.id}`, kind: 'person' as const, id: person.id, label: person.displayName })),
        ...roles.map((role) => ({ key: `role:${role}`, kind: 'role' as const, id: role, label: ROLE_LABEL[role] })),
        ...(targetMode === 'event'
          ? divisions
              .filter((division) => divisionIds.includes(division.id))
              .map((division) => ({ key: `division:${division.id}`, kind: 'division' as const, id: division.id, label: division.name }))
          : []),
        ...(targetMode === 'event'
          ? teams
              .filter((team) => teamIds.includes(team.id))
              .map((team) => ({ key: `team:${team.id}`, kind: 'team' as const, id: team.id, label: team.name }))
          : []),
      ];
  const removeRecipientChip = (chip: RecipientChip) => {
    if (chip.kind === 'directory') setAllDirectorySelected(false);
    if (chip.kind === 'event') updateEventFilter('');
    if (chip.kind === 'person') updateProfileIds(profileIds.filter((id) => id !== chip.id));
    if (chip.kind === 'role') updateRoles(roles.filter((role) => role !== chip.id));
    if (chip.kind === 'division') updateDivisionIds(divisionIds.filter((id) => id !== chip.id));
    if (chip.kind === 'team') updateTeamIds(teamIds.filter((id) => id !== chip.id));
  };
  const toMetaLabel = hasRecipientCriteria
    ? `${selectedRecipientCount} recipient${selectedRecipientCount === 1 ? '' : 's'} selected`
    : 'Add recipients';
  const canGenerateDraft =
    allowed &&
    !assisting &&
    hasRecipientCriteria &&
    selectedRecipientCount > 0 &&
    (Boolean(assistBrief.trim()) || Boolean(body.trim()));

  const onAssist = async () => {
    const bodyText = body.trim();
    const briefText = assistBrief.trim();
    const useBodyAsPrompt = !briefText && !subject.trim() && looksLikeAssistInstruction(bodyText);
    const action = briefText || useBodyAsPrompt ? 'draft' : 'rewrite_clarity';
    const brief = briefText || (useBodyAsPrompt ? bodyText : '');

    if (!brief && !bodyText && !subject.trim()) {
      setError('Tell Scorekeeper Intelligence what you want to send first.');
      return;
    }
    if (!hasRecipientCriteria || selectedRecipientCount === 0) {
      setError('Choose at least one recipient before generating a draft.');
      return;
    }
    setAssisting(true);
    setNotice('');
    setError('');
    setAssistNotes('');
    try {
      const response = await fetch('/api/communications/assist', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action,
          brief,
          subject,
          body: useBodyAsPrompt ? '' : body,
          tournamentId: targetMode === 'event' ? selectedTournament?.id : undefined,
          audience: sendAudience,
          channels,
        }),
      });
      const data = (await response.json().catch(() => ({}))) as AssistResponse;
      if (!response.ok || data.ok === false || !data.draft) {
        throw new Error(communicationAssistError(data, response.status));
      }
      setSubject(data.draft.subject);
      setBody(data.draft.body);
      setAssistNotes(data.draft.notes);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not generate communication draft.');
    } finally {
      setAssisting(false);
    }
  };

  const onSend = async () => {
    if (!subject.trim() || !body.trim() || !hasRecipientCriteria) return;
    setSending(true);
    setNotice('');
    setError('');
    try {
      const response = await fetch('/api/communications/send', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tournamentId: targetMode === 'event' ? selectedTournament?.id : undefined,
          authorProfileId: profile.id,
          subject,
          body,
          audience: sendAudience,
          channels,
          postAsAnnouncement: targetMode === 'event' && postAsAnnouncement,
        }),
      });
      const data = (await response.json().catch(() => ({}))) as SendResponse;
      if (data.db) setDb(data.db);
      if (!response.ok || data.ok === false) {
        throw new Error(data.error ?? `Send failed with ${response.status}`);
      }
      setNotice(
        `Sent to ${data.summary?.contactableRecipients ?? audiencePreview.sendableRecipients.length} recipient${
          (data.summary?.contactableRecipients ?? audiencePreview.sendableRecipients.length) === 1 ? '' : 's'
        }.`,
      );
      resetComposer();
      setTab('inbox');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send communication.');
    } finally {
      setSending(false);
    }
  };

  return (
    <AdminModulePage>
      <PageHero
        title="Draft From the Directory"
        description="Choose a targeted Directory audience, draft the message, send it, and keep the audit trail in one place."
        icon={Megaphone}
        accent="arc"
      />

      <div className="tabs-bar" role="tablist" aria-label="Communication Hub sections">
        {[
          ['compose', 'Compose'],
          ['inbox', 'Inbox'],
          ['sent', 'Sent'],
        ].map(([id, label]) => (
          <button
            key={id}
            className={`tabs-bar__tab ${tab === id ? 'is-active' : ''}`}
            onClick={() => setTab(id as HubTab)}
            type="button"
          >
            {label}
          </button>
        ))}
      </div>

      {notice && <div className="note note--ok" style={{ marginBottom: 14 }}>{notice}</div>}
      {error && <div className="note note--warn" style={{ marginBottom: 14 }}>{error}</div>}

      {tab === 'inbox' ? (
        <CommunicationInbox mode="admin" />
      ) : tab === 'sent' ? (
        <SentAudit messages={messages} db={db} />
      ) : (
        <div className="comm-compose-shell">
          {!senderInDirectory && (
            <div className="note note--warn">
              Your active admin account must be an active Directory profile before it can send league communications.
            </div>
          )}
          {targetMode === 'event' && !selectedTournament && (
            <div className="note note--warn">
              No messageable event is selected. Choose a scheduled, live, paused, or completed event, or switch the recipient scope to the full Directory.
            </div>
          )}
          {targetMode === 'event' && selectedTournament && !isSelectedMessageable && (
            <div className="note note--warn">
              This event is not ready for audience messaging yet. Scheduled, live, paused, and completed events can be messaged.
            </div>
          )}

          <section className="card comm-compose">
            <div className="comm-compose__head">
              <div>
                <h3 className="card__title">Compose</h3>
                <p className="card__sub">
                  Directory is the source of truth for every recipient. Add people, roles, teams, or an explicit full-Directory send.
                </p>
              </div>
            </div>

            <div className="comm-field-row comm-field-row--single comm-field-row--to">
              <div
                className={`comm-to-button ${recipientFlyoutOpen ? 'is-open' : ''}`}
                onClick={() => {
                  if (!canAdmin || sending) return;
                  setRecipientFlyoutOpen((value) => !value);
                }}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter' && event.key !== ' ') return;
                  event.preventDefault();
                  if (!canAdmin || sending) return;
                  setRecipientFlyoutOpen((value) => !value);
                }}
                role="button"
                tabIndex={!canAdmin || sending ? -1 : 0}
                aria-label={`To: ${toMetaLabel}`}
                aria-expanded={recipientFlyoutOpen}
                aria-disabled={!canAdmin || sending}
              >
                <span className="comm-token-list">
                  {recipientChips.length === 0 ? (
                    <span className="comm-placeholder">To</span>
                  ) : (
                    <>
                      {recipientChips.slice(0, 5).map((chip) => (
                        <span key={chip.key} className="comm-token">
                          <span>{chip.label}</span>
                          <button
                            type="button"
                            className="comm-token-remove"
                            aria-label={`Remove ${chip.label}`}
                            onClick={(event) => {
                              event.stopPropagation();
                              removeRecipientChip(chip);
                            }}
                            disabled={sending}
                          >
                            <X size={12} />
                          </button>
                        </span>
                      ))}
                      {recipientChips.length > 5 && <span className="comm-token">+{recipientChips.length - 5}</span>}
                    </>
                  )}
                </span>
                <span className="comm-to-meta" aria-hidden="true">
                  {toMetaLabel}
                  <ChevronDown size={14} />
                </span>
              </div>
            </div>

            {recipientFlyoutOpen && (
              <RecipientFlyout
                allowed={Boolean(canAdmin) && !sending}
                allDirectorySelected={allDirectorySelected}
                clearRecipients={clearRecipients}
                close={() => setRecipientFlyoutOpen(false)}
                divisions={divisions}
                divisionIds={divisionIds}
                events={messageableTournaments}
                people={people}
                personQuery={personQuery}
                preview={audiencePreview}
                previewRecipients={previewRecipients}
                profileIds={profileIds}
                roles={roles}
                toggleAllDirectory={toggleAllDirectory}
                setDivisionIds={updateDivisionIds}
                setPersonQuery={setPersonQuery}
                setProfileIds={updateProfileIds}
                setRoles={updateRoles}
                setEventFilter={updateEventFilter}
                setTeamIds={updateTeamIds}
                targetMode={targetMode}
                teamIds={teamIds}
                teams={teams}
                tournamentId={tournamentId}
              />
            )}

            <div className="comm-field-row comm-field-row--single">
              <input
                className="comm-plain-input"
                placeholder="Subject"
                value={subject}
                onChange={(event) => setSubject(event.target.value)}
                disabled={!allowed || sending}
              />
            </div>

            <div className="comm-body-area">
              <textarea
                className="comm-message-input"
                placeholder="Write the email here, or describe what you want below and generate a draft."
                rows={10}
                value={body}
                onChange={(event) => setBody(event.target.value)}
                disabled={!allowed || sending}
              />
              <div className="comm-generate-bar">
                <Sparkles size={15} />
                <input
                  className="comm-generate-input"
                  value={assistBrief}
                  onChange={(event) => setAssistBrief(event.target.value)}
                  placeholder="What do you want to send? Example: Tell teams game times were updated."
                  disabled={!allowed || assisting}
                />
                <button
                  type="button"
                  className={`btn btn--primary btn--sm ${assisting ? 'is-loading' : ''}`}
                  onClick={onAssist}
                  disabled={!canGenerateDraft}
                  aria-busy={assisting}
                >
                  {assisting ? <><Loader2 size={14} className="spin" /> Generating...</> : 'Generate'}
                </button>
              </div>
              {assistNotes && <div className="note" style={{ marginTop: 10 }}>{assistNotes}</div>}
            </div>

            <div className="comm-compose-options">
              <div className="row" style={{ flexWrap: 'wrap', gap: 12 }}>
                {(['email', 'in_app'] as const).map((channel) => (
                  <label key={channel} className="row" style={{ gap: 6, alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      checked={channels.includes(channel)}
                      onChange={(event) => {
                        setChannels((prev) =>
                          event.target.checked
                            ? Array.from(new Set([...prev, channel]))
                            : prev.filter((item) => item !== channel),
                        );
                      }}
                      disabled={!allowed || sending}
                    />
                    <span>{channel === 'email' ? 'Email' : 'In-app inbox'}</span>
                  </label>
                ))}
                {targetMode === 'event' && (
                  <label className="row" style={{ gap: 6, alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      checked={postAsAnnouncement}
                      onChange={(event) => setPostAsAnnouncement(event.target.checked)}
                      disabled={!allowed || sending}
                    />
                    <span><Pin size={12} style={{ verticalAlign: -2 }} /> Public announcement</span>
                  </label>
                )}
              </div>
            </div>

            <div className="comm-compose__footer">
              <SendSummary
                channels={channels}
                deliveryWarnings={deliveryWarnings}
                preview={audiencePreview}
                targetLabel={targetLabel}
              />
              <div className="comm-compose__actions">
                <button type="button" className="btn btn--ghost" onClick={resetComposer} disabled={sending}>
                  Clear
                </button>
                <LoadingButton
                  variant="primary"
                  loading={sending}
                  loadingLabel="Sending..."
                  disabled={!allowed || sending || !hasRecipientCriteria || !subject.trim() || !body.trim() || channels.length === 0 || audiencePreview.sendableRecipients.length === 0}
                  onClick={() => setSendConfirmOpen(true)}
                >
                  <Send size={14} /> Send now
                </LoadingButton>
              </div>
            </div>
          </section>
        </div>
      )}
      <ConfirmDialog
        open={Boolean(leaveHref)}
        title="Leave Communication Hub?"
        body={COMPOSER_LEAVE_WARNING}
        confirmLabel="Leave page"
        tone="warn"
        onCancel={() => setLeaveHref('')}
        onConfirm={() => {
          const href = leaveHref;
          setLeaveHref('');
          resetComposer();
          window.location.href = href;
        }}
      />
      <ConfirmDialog
        open={sendConfirmOpen}
        title={`Send ${targetLabel.toLowerCase()} message?`}
        body={`${audiencePreview.sendableRecipients.length} recipient${audiencePreview.sendableRecipients.length === 1 ? '' : 's'} selected. ${audiencePreview.skippedDeliveries} channel delivery${audiencePreview.skippedDeliveries === 1 ? '' : 'ies'} will be skipped.`}
        confirmLabel="Send now"
        tone="warn"
        onCancel={() => setSendConfirmOpen(false)}
        onConfirm={() => {
          setSendConfirmOpen(false);
          void onSend();
        }}
      />
    </AdminModulePage>
  );
}

function RecipientFlyout(props: {
  allowed: boolean;
  allDirectorySelected: boolean;
  clearRecipients: () => void;
  close: () => void;
  events: Array<{ id: string; name: string; format?: string; status: string }>;
  people: RecipientPersonOption[];
  personQuery: string;
  preview: AudiencePreview;
  previewRecipients: RecipientPersonOption[];
  profileIds: string[];
  roles: Role[];
  toggleAllDirectory: () => void;
  targetMode: TargetMode;
  tournamentId: string;
  setEventFilter: (id: string) => void;
  setRoles: (roles: Role[]) => void;
  divisions: Array<{ id: string; name: string }>;
  divisionIds: string[];
  setDivisionIds: (ids: string[]) => void;
  teams: Array<{ id: string; name: string }>;
  teamIds: string[];
  setTeamIds: (ids: string[]) => void;
  setPersonQuery: (value: string) => void;
  setProfileIds: (ids: string[]) => void;
}) {
  const [showAllPreview, setShowAllPreview] = useState(false);
  const selectedCount = props.preview.items.length;
  const totalMatches = props.preview.items.length;
  const unavailableCount = props.preview.blockedRecipients.length;
  const warningCount = props.preview.warningRecipients.length + unavailableCount;
  const hasRecipients =
    props.allDirectorySelected ||
    props.targetMode === 'event' ||
    props.roles.length > 0 ||
    props.divisionIds.length > 0 ||
    props.teamIds.length > 0 ||
    props.profileIds.length > 0;
  const previewLimit = showAllPreview ? props.previewRecipients.length : RECIPIENT_PREVIEW_LIMIT;
  const visiblePreviewRecipients = props.previewRecipients.slice(0, previewLimit);
  const hiddenPreviewCount = Math.max(0, props.previewRecipients.length - visiblePreviewRecipients.length);

  return (
    <div className="comm-recipient-flyout overlay-panel" role="dialog" aria-label="Choose recipients">
      <div className="comm-flyout__head overlay-head">
        <div>
          <h3 className="card__title">
            <Users size={16} style={{ verticalAlign: -3, marginRight: 6 }} />
            Choose recipients
          </h3>
          <p className="card__sub">
            Search Directory people or build an audience from events, roles, divisions, and teams.
          </p>
        </div>
        <button type="button" className="btn btn--ghost btn--icon" onClick={props.close} aria-label="Close recipient picker">
          <X size={16} />
        </button>
      </div>

      <div className="comm-recipient-flyout__body overlay-body touch-scroll">
        <section className="comm-recipient-section" aria-label="Audience filters">
          <div>
            <h4>Audience filters</h4>
            <p>Use filters for event, role, division, or team audiences.</p>
          </div>
          <div className="comm-filter-grid">
            <div className="comm-filter-field">
              <div className="field__label">Event</div>
              <select
                className="comm-filter-control"
                value={props.targetMode === 'event' ? props.tournamentId : ''}
                onChange={(event) => props.setEventFilter(event.target.value)}
                disabled={!props.allowed}
              >
                <option value="">No event filter</option>
                {props.events.map((event) => (
                  <option key={event.id} value={event.id}>{event.name}</option>
                ))}
              </select>
              {props.events.length === 0 && <div className="comm-filter-help">No messageable events yet.</div>}
            </div>

            <MultiToggleGroup
              disabled={!props.allowed}
              items={ALL_ROLES.filter((role) => role !== 'staff').map((role) => ({ id: role, label: ROLE_LABEL[role] }))}
              label="Roles"
              selected={props.roles}
              onChange={(ids) => props.setRoles(ids as Role[])}
            />

            <MultiToggleGroup
              disabled={!props.allowed || props.targetMode !== 'event'}
              empty={props.targetMode === 'event' ? 'No divisions yet.' : 'Choose an event first.'}
              items={props.divisions.map((division) => ({ id: division.id, label: division.name }))}
              label="Divisions"
              selected={props.divisionIds}
              onChange={props.setDivisionIds}
            />

            <MultiToggleGroup
              disabled={!props.allowed || props.targetMode !== 'event'}
              empty={props.targetMode === 'event' ? 'No teams yet.' : 'Choose an event first.'}
              items={props.teams.map((team) => ({ id: team.id, label: team.name }))}
              label="Teams"
              selected={props.teamIds}
              onChange={props.setTeamIds}
            />
          </div>
        </section>

        <section className="comm-recipient-section" aria-label="Selected recipient preview">
          <div>
            <h4>{selectedCount} recipient{selectedCount === 1 ? '' : 's'} selected</h4>
            <p>Preview who matches the current audience before you send.</p>
          </div>
          {visiblePreviewRecipients.length > 0 ? (
            <div className="comm-preview-list">
              {visiblePreviewRecipients.map((person) => (
                <div key={person.id} className="comm-preview-row">
                  <span>
                    <strong>{person.displayName}</strong>
                    <span>{person.email ?? 'No email on file'}</span>
                  </span>
                  <span className="comm-person-meta">
                    {person.roleLabels.map((label) => <span key={label} className="chip">{label}</span>)}
                    {person.lastEventName && <span className="comm-last-event">Last event: {person.lastEventName}</span>}
                  </span>
                </div>
              ))}
              {props.previewRecipients.length > RECIPIENT_PREVIEW_LIMIT && (
                <button type="button" className="btn btn--ghost btn--sm comm-show-more" onClick={() => setShowAllPreview((value) => !value)}>
                  {showAllPreview ? 'Show fewer' : `Show ${hiddenPreviewCount} more`}
                </button>
              )}
            </div>
          ) : hasRecipients ? (
            <div className="note note--warn">No active Directory profiles match these recipients.</div>
          ) : (
            <div className="comm-empty-line">Choose filters, people, or everyone in Directory to preview recipients.</div>
          )}
        </section>

        <section className="comm-recipient-section" aria-label="People recipients">
          <div>
            <h4>People</h4>
            <p>Type a name or email, then select one or more active Directory people.</p>
          </div>
          <div className="comm-search-row">
            <Search size={15} />
            <input
              value={props.personQuery}
              onChange={(event) => props.setPersonQuery(event.target.value)}
              placeholder="Search Directory by name or email"
              disabled={!props.allowed}
            />
          </div>
          <div className="comm-option-list" role="listbox" aria-label="Directory people">
            {props.people.map((person) => {
              const selected = props.profileIds.includes(person.id);
              return (
                <button
                  key={person.id}
                  type="button"
                  className={`comm-option-row ${selected ? 'is-selected' : ''}`}
                  onClick={() => {
                    props.setProfileIds(
                      selected
                        ? props.profileIds.filter((id) => id !== person.id)
                        : [...props.profileIds, person.id],
                    );
                  }}
                  disabled={!props.allowed}
                  aria-pressed={selected}
                >
                  <span className="comm-option-check">{selected && <Check size={14} aria-hidden="true" />}</span>
                  <span className="comm-person-main">
                    <strong>{person.displayName}</strong>
                    <span>{person.email ?? 'No email on file'}</span>
                    <span className="comm-person-meta">
                      {person.roleLabels.map((label) => <span key={label} className="chip">{label}</span>)}
                      {person.lastEventName && <span className="comm-last-event">Last event: {person.lastEventName}</span>}
                    </span>
                  </span>
                </button>
              );
            })}
            {props.people.length === 0 && <span className="muted">No active Directory people match that search.</span>}
          </div>
        </section>

        <section className="comm-recipient-section" aria-label="Full Directory audience">
          <div>
            <h4>Full Directory</h4>
            <p>Use this only when the whole active Directory should receive the message.</p>
          </div>
          <button
            type="button"
            className={`comm-audience-choice ${props.allDirectorySelected ? 'is-active' : ''}`}
            onClick={props.toggleAllDirectory}
            disabled={!props.allowed}
            aria-pressed={props.allDirectorySelected}
          >
            <span className="comm-option-check">{props.allDirectorySelected && <Check size={14} aria-hidden="true" />}</span>
            <span>
              <strong>Everyone in Directory</strong>
              <span>{props.allDirectorySelected ? 'Selected. Click again to remove this audience.' : 'Send to every active Directory profile.'}</span>
            </span>
          </button>
        </section>
      </div>

      <div className="comm-recipient-flyout__footer overlay-footer">
        <button type="button" className="btn btn--ghost btn--sm" onClick={props.clearRecipients} disabled={!props.allowed || !hasRecipients}>
          Clear all
        </button>
        <div className="comm-recipient-summary">
          <strong>
            {hasRecipients
              ? `${selectedCount} recipient${selectedCount === 1 ? '' : 's'} selected`
              : 'No recipients selected'}
          </strong>
          {warningCount > 0 ? (
            <span>{warningCount} selected profile{warningCount === 1 ? '' : 's'} need attention before every channel can be delivered.</span>
          ) : totalMatches > 0 ? (
            <span>Ready for the selected delivery channels.</span>
          ) : hasRecipients ? (
            <span>No active Directory profiles match these recipients.</span>
          ) : (
            <span>Choose audience filters, people, or everyone in Directory.</span>
          )}
        </div>
        <button type="button" className="btn btn--primary btn--sm" onClick={props.close}>
          Apply recipients
        </button>
      </div>
    </div>
  );
}

function MultiToggleGroup(props: {
  label: string;
  items: Array<{ id: string; label: string }>;
  selected: string[];
  onChange: (ids: string[]) => void;
  disabled: boolean;
  empty?: string;
}) {
  const toggle = (id: string) => {
    props.onChange(
      props.selected.includes(id)
        ? props.selected.filter((item) => item !== id)
        : [...props.selected, id],
    );
  };

  return (
    <div className="comm-filter-field">
      <div className="field__label">{props.label}</div>
      <div className={`comm-toggle-list ${props.disabled ? 'is-disabled' : ''}`}>
        {props.items.map((item) => {
          const selected = props.selected.includes(item.id);
          return (
            <button
              key={item.id}
              type="button"
              className={`comm-toggle-pill ${selected ? 'is-selected' : ''}`}
              onClick={() => toggle(item.id)}
              disabled={props.disabled}
              aria-pressed={selected}
            >
              {item.label}
            </button>
          );
        })}
        {props.items.length === 0 && <span className="comm-filter-help">{props.empty ?? 'No options yet.'}</span>}
      </div>
    </div>
  );
}

function SendSummary(props: {
  channels: ActiveCommunicationChannel[];
  deliveryWarnings: number;
  preview: AudiencePreview;
  targetLabel: string;
}) {
  const selectedCount = props.preview.items.length;
  const attentionCount = props.preview.warningRecipients.length + props.preview.blockedRecipients.length;
  return (
    <div className="comm-send-summary">
      <strong>{selectedCount} recipient{selectedCount === 1 ? '' : 's'} selected</strong>
      <span>
        {props.targetLabel} - {props.channels.map((channel) => (channel === 'email' ? 'Email' : 'In-app')).join(' + ') || 'No channel selected'}
      </span>
      {props.deliveryWarnings > 0 && (
        <div className="note note--warn">
          {attentionCount} recipient{attentionCount === 1 ? '' : 's'} need attention. {props.preview.skippedDeliveries} channel delivery{props.preview.skippedDeliveries === 1 ? '' : 'ies'} will be skipped.
        </div>
      )}
    </div>
  );
}

function SentAudit({ messages, db }: { messages: ReturnType<typeof listCommunications>; db: AppDatabase }) {
  const [selectedMessageId, setSelectedMessageId] = useState('');
  const selectedMessage = messages.find((message) => message.id === selectedMessageId) ?? messages[0];
  const selectedDeliveries = selectedMessage
    ? db.communicationDeliveries.filter((delivery) => delivery.communicationId === selectedMessage.id)
    : [];

  useEffect(() => {
    if (!selectedMessageId && messages[0]) setSelectedMessageId(messages[0].id);
    if (selectedMessageId && !messages.some((message) => message.id === selectedMessageId)) {
      setSelectedMessageId(messages[0]?.id ?? '');
    }
  }, [selectedMessageId, messages]);

  if (messages.length === 0) {
    return (
      <div className="empty">
        <Send size={18} />
        <div style={{ fontWeight: 700, color: 'var(--text)', marginTop: 8 }}>No sent threads yet.</div>
        <p className="muted" style={{ marginTop: 6 }}>
          Sent communications and delivery history will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid--2">
      <div className="card">
        <div className="card__head">
          <div>
            <h3 className="card__title">
              <Send size={16} style={{ verticalAlign: -3, marginRight: 6 }} />
              Sent
            </h3>
            <p className="card__sub">{messages.length} thread{messages.length === 1 ? '' : 's'}</p>
          </div>
        </div>
        <div className="stack">
          {messages.map((message) => {
            const event = message.tournamentId ? db.tournaments.find((tournament) => tournament.id === message.tournamentId) : null;
            const deliveries = db.communicationDeliveries.filter((delivery) => delivery.communicationId === message.id);
            return (
              <button
                key={message.id}
                className={`thread-row ${selectedMessage?.id === message.id ? 'is-active' : ''}`}
                onClick={() => setSelectedMessageId(message.id)}
                type="button"
              >
                <span>
                  <strong>{message.subject}</strong>
                  <span className="muted">
                    {event?.name ?? 'Full Directory'} - {deliveries.length} delivery{deliveries.length === 1 ? '' : 'ies'}
                  </span>
                </span>
                <span className="muted">{message.sentAt ? new Date(message.sentAt).toLocaleDateString() : '-'}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="card">
        {selectedMessage && (
          <>
            <div className="card__head">
              <div>
                <h3 className="card__title">{selectedMessage.subject}</h3>
                <p className="card__sub">
                  {selectedDeliveries.length} delivery{selectedDeliveries.length === 1 ? '' : 'ies'} - {selectedMessage.status}
                </p>
              </div>
            </div>

            <div className="stack" style={{ marginBottom: 14 }}>
              <div className="message-bubble">
                <div className="row row--between" style={{ gap: 10 }}>
                  <strong>{db.profiles.find((item) => item.id === selectedMessage.authorProfileId)?.displayName ?? 'League HQ'}</strong>
                  <span className="muted" style={{ fontSize: 11 }}>
                    {selectedMessage.sentAt ? new Date(selectedMessage.sentAt).toLocaleString() : '-'}
                  </span>
                </div>
                <div style={{ fontWeight: 700, marginTop: 6 }}>{selectedMessage.subject}</div>
                <p style={{ margin: '6px 0 0', whiteSpace: 'pre-wrap' }}>{selectedMessage.body}</p>
                <div className="row" style={{ gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                  {selectedMessage.channels.includes('email') && <span className="chip"><Mail size={11} /> email</span>}
                  {selectedMessage.channels.includes('in_app') && <span className="chip"><MessageSquare size={11} /> in-app</span>}
                </div>
              </div>
            </div>

            {selectedDeliveries.length > 0 && (
              <div className="note">
                Delivery audit: {selectedDeliveries.filter((d) => d.status === 'sent').length} sent,{' '}
                {selectedDeliveries.filter((d) => d.status === 'queued').length} queued,{' '}
                {selectedDeliveries.filter((d) => d.status === 'skipped').length} skipped,{' '}
                {selectedDeliveries.filter((d) => d.status === 'failed').length} failed.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
